import type { Countdown, Wedding } from '../types';
import ColibriLogo from './ColibriLogo';
import HeartOrnament from './HeartOrnament';

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function coupleLabel(wedding: Wedding | null): string {
  if (!wedding) return '';
  const name = (wedding.name || '').trim();
  const partner = (wedding.partner_name || '').trim();
  const cleaned = name.replace(/^Свадьба\s+/i, '').replace(/^Свадьба:\s*/i, '');
  if (partner && !cleaned.toLowerCase().includes(partner.toLowerCase())) {
    return `${cleaned} и ${partner}`;
  }
  return cleaned;
}

type AnimId = 'hearts' | 'pulse' | 'glow' | 'bounce' | 'off';

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function RingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="14" r="5.2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="15" cy="14" r="5.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 6.5l1.2-2.2h2.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function CountdownHero({
  wedding,
  countdown,
  now,
  compact = false,
}: {
  wedding: Wedding | null;
  countdown: Countdown | null;
  now: number;
  compact?: boolean;
}) {
  const imageId = countdown?.image_id || 1;
  const shapeId = countdown?.shape_id || 4;
  const animId: AnimId =
    countdown?.animation === false ? 'off' : 'hearts';
  // countdown loads async — don't flash default photo before the user's pick arrives
  const bgClass = countdown ? `cd-bg-${imageId}` : 'cd-bg-loading';

  const targetTs = wedding?.wedding_date ? new Date(wedding.wedding_date).getTime() : null;
  const remainingMs = targetTs !== null ? Math.max(0, targetTs - now) : null;
  const days = remainingMs !== null ? Math.floor(remainingMs / 86400000) : null;
  const hh = remainingMs !== null ? Math.floor(remainingMs / 3600000) % 24 : 0;
  const mm = remainingMs !== null ? Math.floor(remainingMs / 60000) % 60 : 0;
  const ss = remainingMs !== null ? Math.floor(remainingMs / 1000) % 60 : 0;

  const phrase = countdown?.phrase?.trim() || '';
  const dateLabel = wedding?.wedding_date
    ? new Date(wedding.wedding_date).toLocaleDateString('ru-RU')
    : '';

  return (
    <div
      className={`cd-hero ${bgClass} cd-shape-${shapeId} cd-anim-${animId}${compact ? ' cd-hero--compact' : ''}`}
    >
      <div className="cd-hero__bg" />
      <div className="cd-hero__veil" />
      <div className="cd-deco">
        <span className="cd-heart" /><span className="cd-heart" /><span className="cd-heart" />
        <span className="cd-heart" /><span className="cd-heart" /><span className="cd-heart" />
      </div>
      <div className="cd-rings"><span /><span /></div>

      <div className="cd-hero__brand">
        <ColibriLogo size={compact ? 44 : 52} />
      </div>

      {phrase && !compact && <div className="cd-hero__phrase">{phrase}</div>}

      <div className={compact ? 'cd-glass' : 'cd-hero__stack'}>
        {wedding && (
          <div className="cd-hero__title">
            <RingsIcon />
            <span className="cd-hero__names">{coupleLabel(wedding)}</span>
          </div>
        )}

        <div className="cd-hero__body">
          {phrase && compact && <div className="cd-hero__phrase">{phrase}</div>}
          {days !== null ? (
            <>
              <div className="cd-hero__days-row">
                <span className="cd-hero__days">{days}</span>
                <span className="cd-hero__days-label">дней до свадьбы</span>
              </div>
              <div className="cd-hero__footer">
                {dateLabel && (
                  <div className="cd-hero__date">
                    <CalendarIcon />
                    <span>{dateLabel}</span>
                  </div>
                )}
                <div className="cd-hero__hms">
                  {pad(hh)}:{pad(mm)}:{pad(ss)}
                  <span className="cd-hero__hms-label">часы · минуты · секунды</span>
                </div>
              </div>
            </>
          ) : (
            <div className="cd-hero__phrase">Дата не выбрана</div>
          )}
        </div>

        <HeartOrnament className="cd-hero__ornament" />
      </div>
    </div>
  );
}
